import { Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { isEmpty, pullAt } from '@sama/micro-dash';
import {
  getShapeForEventTracking,
  ShapeState2D,
} from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import {
  addPoints,
  distanceBetween,
} from '../../../../shared/misc-utilities/point-utils';
import {
  Point2D,
  ShapeLocation,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { MouseCoordinateService } from '../../mouse-coordinate.service';
import { getShapeHelper } from '../../shape-helpers/get-shape-helper';
import { ensureKeyLocation, LocationHelper } from '../../utils/location-helper';
import { AbstractDragHandler2D } from '../shared/drag-handlers/abstract-drag-handler-2d';

const deleteRadius = 10; // In container pixels

export class RemoveSubpointDragHandler extends AbstractDragHandler2D {
  protected override event = { id: 'edit_shape_points', bucket: 'adjustment' };
  protected override shouldTrackBatchEvents = true;

  private numberOfDeletedSubpoints = 0;
  private locationHelper: LocationHelper;
  private mouseCoordinateService: MouseCoordinateService;

  constructor(initialEvent: MouseEvent, injector: Injector) {
    super(initialEvent, injector);
    this.mouseCoordinateService = injector.get(MouseCoordinateService);
    this.locationHelper = new LocationHelper(this.store);
  }

  protected async onDrag() {
    const focusedShapeStore = this.store.getFocusedShapeStore();
    if (!focusedShapeStore) {
      return;
    }

    const location = this.locationHelper.determineLocation(
      focusedShapeStore.state().key_locations,
      this.store.state().frameNumber,
    );
    if (!areSubpointsDeletable(focusedShapeStore, location.points)) {
      return;
    }

    const indexesToRemove = await this.determineIndexesToRemove(
      focusedShapeStore,
      location,
    );
    this.deleteSubpoints(focusedShapeStore, indexesToRemove);
  }

  protected override complete() {
    if (this.numberOfDeletedSubpoints === 0) {
      return;
    }

    const focusedShape = this.store.getFocusedShapeStore()?.state();
    assert(focusedShape, 'Deleting subpoints requires a focused shape');
    this.shellConnector.setShapes(this.store.state());
    this.shellConnector.pushAndTrack({
      ...this.event,
      method: 'remove_mode',
      source: 'edit_tool',
      shapes: [getShapeForEventTracking(focusedShape)],
      deleted_subpoint_count: this.numberOfDeletedSubpoints,
      ...this.getBatchValues().buildEventTrackingValue(),
    });
  }

  // eslint-disable-next-line max-lines-per-function -- if you need to modify this code, fix this first
  private async determineIndexesToRemove(
    focusedShapeStore: Store<ShapeState2D>,
    location: ShapeLocation<Point2D>,
  ) {
    const clientMousePoint = this.store.state().mouse.clientPoint;
    const minPoints = getShapeHelper(
      focusedShapeStore.state().type,
    ).getMinPoints();

    const indexesToRemove: number[] = [];
    for (const subpoint of location.points) {
      const index = location.points.indexOf(subpoint);
      const halfPixelSize = this.store.state().pixelSize / 2;
      // adjusting the position of the mouse to the middle of the pixel
      const adjustedSubpoint = addPoints(
        // orthographic outputs in object projects have 3d points, adding a 2d
        // point with a 3d point causes the z value to be NaN, which in turn
        // screws up the distance calculation, that's why we remove the z value
        [subpoint[0], subpoint[1]],
        [halfPixelSize, halfPixelSize],
      );

      const distance = distanceBetween(
        await this.mouseCoordinateService.getClientPointFromImagePoint(
          adjustedSubpoint,
        ),
        clientMousePoint,
      );
      // Remove all subpoints within deleteRadius, but also leave enough points
      // for the shape to be valid even if many points are deleted in one action
      if (
        distance <= deleteRadius &&
        location.points.length - indexesToRemove.length > minPoints
      ) {
        indexesToRemove.push(index);
      }
    }

    return indexesToRemove;
  }

  private deleteSubpoints(
    focusedShapeStore: Store<ShapeState2D>,
    indexesToRemove: number[],
  ) {
    if (isEmpty(indexesToRemove)) {
      return;
    }

    ensureKeyLocation(
      this.locationHelper,
      focusedShapeStore('key_locations'),
      this.store.state().frameNumber,
    )('points').mutateUsing(pullAt, indexesToRemove);
    this.numberOfDeletedSubpoints += indexesToRemove.length;
  }
}

function areSubpointsDeletable(
  shapeStore: Store<ShapeState2D>,
  points: Point2D[],
) {
  return getShapeHelper(shapeStore.state().type).areSubpointsDeletable(points);
}
